/**
 * Unified status vocabulary for form-builder templates (registration forms and
 * assessment templates). Both features converge on `draft | active | archived`;
 * legacy assessment docs may still carry `'published'`, which normalises to
 * `'active'` on read.
 */

export type TemplateStatus = 'draft' | 'active' | 'archived';

/** Presentation for a single status badge (chip + dot colours + label). */
export type TemplateStatusBadge = {
  label: string;
  /** Tailwind classes for a filled pill/chip. */
  chipClass: string;
  /** Tailwind classes for a small status dot. */
  dotClass: string;
};

export const TEMPLATE_STATUS_BADGES: Record<TemplateStatus, TemplateStatusBadge> = {
  draft: {
    label: 'Draft',
    chipClass:
      'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
    dotClass: 'bg-amber-500',
  },
  active: {
    label: 'Active',
    chipClass:
      'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
    dotClass: 'bg-emerald-500',
  },
  archived: {
    label: 'Archived',
    chipClass:
      'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400',
    dotClass: 'bg-neutral-400',
  },
};

/**
 * Normalises any stored status string onto the canonical vocabulary. Legacy
 * `'published'` maps to `'active'`; anything unrecognised falls back to
 * `'draft'`.
 */
export function normaliseTemplateStatus(raw: unknown): TemplateStatus {
  if (raw === 'published') return 'active';
  if (raw === 'active' || raw === 'archived' || raw === 'draft') return raw;
  return 'draft';
}

/** Badge presentation for a (possibly un-normalised) status value. */
export function templateStatusBadge(status: unknown): TemplateStatusBadge {
  return TEMPLATE_STATUS_BADGES[normaliseTemplateStatus(status)];
}
